import { WarningAmberOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import React, { Fragment, type MouseEvent, memo } from "react";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { STATUS } from "../../../../theme/tokens";
import type { DepartmentLocation, Nurse, NurseRole, RecurringShift } from "../../../../utils/models";
import { ShiftTypeIcon } from "../auxillary/ShiftTypeIcon";
import { DroppableCell } from "../Cells/DroppableCell";
import { LocationCell } from "../Cells/LocationCell";
import {
	getDayExpectedAmount,
	getDayKey,
	getRowGridSx,
	gridCellShiftTimesStyle,
	gridShiftCellWrapperStyle,
	type ScheduleData,
	type ScheduleWeekData,
	type Section,
} from "../utils";
import { GridDivider } from "./GridDivider";

type Props = {
	rowKey: string;
	currentLocation: DepartmentLocation;
	isCollapsed: boolean;
	amountOfWeeks: number;
	activeDragId: string | null;
	departmentSpecificRoles: NurseRole[];
	refetchRecurringShifts: (options?: RefetchOptions) => Promise<QueryObserverResult<RecurringShift[], Error>>;
	handleOnAddClick: (clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	uniqueSections: Section[];
	onShiftClick: (recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	onShiftKeyDown: (recurringShift: RecurringShift, event: React.KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	scheduleData: ScheduleData;
	currentWeeklyShifts: ScheduleWeekData[];
	onCtrlPressedChange?: (isCtrlPressed: boolean) => void;
	isSynergyMS?: boolean;
	isDraggingSection: boolean;
	getNurseById: (id: number) => Nurse | undefined;
	isFadedOut?: boolean;
	isHidden?: boolean;
	isLastRow: boolean;
};

// motion-ize Box so it still accepts `sx`
const MBox = motion.create(Box);

export const MasterScheduleRow = memo<Props>(
	({
		rowKey,
		currentLocation,
		isCollapsed,
		amountOfWeeks,
		activeDragId,
		departmentSpecificRoles,
		refetchRecurringShifts,
		handleOnAddClick,
		uniqueSections,
		onShiftClick,
		onShiftKeyDown,
		scheduleData,
		currentWeeklyShifts,
		onCtrlPressedChange,
		isSynergyMS = false,
		isDraggingSection,
		getNurseById,
		isFadedOut = false,
		isHidden = false,
		isLastRow,
	}) => {
		const moreShiftsThanExpectedInSomeDay = currentWeeklyShifts.some((week) =>
			week.days.some((day) => day.recurringShiftsInCell.length > getDayExpectedAmount(currentLocation, day.dayDate)),
		);
		return (
			<AnimatePresence key={`overlay-row-${rowKey}`} initial={false}>
				{!isCollapsed && !isHidden && (
					<MBox
						key={`overlay-row-${rowKey}`}
						id={`overlay-row-${rowKey}`}
						data-testid={`master-schedule-row-${rowKey}`}
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
						sx={{
							...getRowGridSx(amountOfWeeks, !!activeDragId || isDraggingSection, isSynergyMS),
							...(isFadedOut && { maskImage: "linear-gradient(to bottom, black 0%, transparent 100%)" }),
						}}
					>
						<LocationCell
							refreshRecurringShifts={refetchRecurringShifts}
							currentLocation={currentLocation}
							departmentSpecificRoles={departmentSpecificRoles}
							isLocationOutsideOfConfig={scheduleData.isLocationOutsideOfConfig ?? false}
							weeklyShifts={currentWeeklyShifts}
							uniqueSections={uniqueSections}
							isSynergyMS={isSynergyMS}
						/>
						<Box sx={gridCellShiftTimesStyle}>
							{isSynergyMS ? (
								(() => {
									// Count total shifts across all weeks and days
									const totalShifts = currentWeeklyShifts.reduce((weekSum, week) => {
										return (
											weekSum +
											week.days.reduce((daySum, day) => {
												return daySum + day.recurringShiftsInCell.length;
											}, 0)
										);
									}, 0);
									// Divide by number of weeks to get average per week
									const shiftsPerWeek = Math.round(currentWeeklyShifts.length > 0 ? totalShifts / currentWeeklyShifts.length : 0);
									const targetVisits = currentLocation.amountRequired ?? 0;
									const missing = shiftsPerWeek < targetVisits;
									return (
										<Box
											sx={{
												display: "flex",
												gap: 1,
												alignItems: "center",
											}}
										>
											<InHouseTooltip title={"Shifts per week (AVG) / Target Visits per week"}>
												<span>
													{shiftsPerWeek} / {targetVisits}
												</span>
											</InHouseTooltip>
											{missing && (
												<InHouseTooltip title={"Missing Shifts"}>
													<WarningAmberOutlined
														fontSize={"inherit"}
														sx={{
															color: alpha(STATUS.ERROR.main, 0.8),
														}}
													/>
												</InHouseTooltip>
											)}
										</Box>
									);
								})()
							) : (
								<>
									{DateTime.fromFormat(currentLocation.shift_start.substring(0, 5), "HH:mm").toFormat("h:mma")} -{" "}
									{DateTime.fromFormat(currentLocation.shift_end.substring(0, 5), "HH:mm").toFormat("h:mma")}
									<ShiftTypeIcon shiftType={scheduleData.shiftType} />
								</>
							)}
						</Box>
						{currentWeeklyShifts.map((weeklyShift, weekIdx) =>
							weeklyShift.days.map((day, dayIdx) => {
								const colIndex = 2 + weekIdx * 7 + dayIdx; // 2 leading columns
								const dayKey = getDayKey(day.dayDate);
								const dayAmount = getDayExpectedAmount(currentLocation, day.dayDate);
								const unfilledSlotsAmount = currentLocation.days?.includes(dayKey) ? dayAmount - day.recurringShiftsInCell.length : 0;
								return (
									<Fragment key={`${weeklyShift.weekNumber}_${day.dayNumber}`}>
										<Box
											data-week-index={weekIdx}
											className="ms2-grid-cell"
											data-testid={`master-schedule-slot-${weekIdx}-${dayIdx}`}
											sx={{
												...gridShiftCellWrapperStyle,
												...(moreShiftsThanExpectedInSomeDay
													? {
															justifyContent: "start",
														}
													: {}),
											}}
										>
											<DroppableCell
												day={day}
												onShiftClick={onShiftClick}
												onShiftKeyDown={onShiftKeyDown}
												cellDepartmentLocation={currentLocation}
												colIndex={colIndex}
												handleOnAddClick={handleOnAddClick}
												unfilledSlotsAmount={unfilledSlotsAmount}
												isBlocked={day.recurringShiftsInCell.length === 0 && unfilledSlotsAmount === 0}
												isDraggingInContext={!!activeDragId}
												onCtrlPressedChange={onCtrlPressedChange}
												isSynergyMS={isSynergyMS}
												isDraggingSection={isDraggingSection}
												getNurseById={getNurseById}
											/>
										</Box>
										{dayIdx === 6 && weekIdx < amountOfWeeks - 1 && <GridDivider overflowHeight={!isLastRow} />}
									</Fragment>
								);
							}),
						)}
					</MBox>
				)}
			</AnimatePresence>
		);
	},
);
